/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';
import { Timestamp } from 'firebase/firestore';

import Part, { partSchema } from './part.js';

/**
 * Suggested piston sizes / flash gaps (Notion's former dropdown values), in
 * Autosuggest option shape. Free text is still allowed on both fields.
 */
export const PistonSizeOptions = Object.freeze([
  { value: 'Double Slide' },
  { value: 'Microplate' },
]);

export const PistonFlashGapOptions = Object.freeze([
  { value: '200 µm' },
  { value: '300 µm' },
  { value: '400 µm' },
  { value: '450 µm' },
  { value: 'Horizontal' },
]);

/**
 * A forming piston. Like a tool, it is one physical instance of a design:
 * `name` is the design, `instance` numbers the copies, and the pPartId is
 * `partId-revision-name-instance` (see `getPPartId`). pPartIds are decoded
 * by splitting on "-", so the name must not contain one. Size and flash gap
 * mirror the fields previously tracked in Notion (its "Design Version" maps
 * to `revision`); both are free text with suggested values, and optional.
 */
export const pistonSchema = partSchema.shape({
  name: Yup.string()
    .required('Name is required')
    .matches(/^[^-]+$/, 'Piston name cannot contain "-"'),
  instance: Yup.number().required('Instance is required'),
  size: Yup.string().default(''),
  flashGap: Yup.string().default(''),
});

/**
 * @typedef {Yup.InferType<typeof pistonSchema>} PistonSchema
 * @typedef {BaseDocData & PistonSchema & { createdAt: Timestamp; updatedAt: Timestamp; }} PistonDocData
*/

/**
 * Class representing a piston.
 * @class
 * @extends Part
 */
export default class Piston extends Part {
  /** @type {PistonDocData['instance']} */ instance;
  /** @type {PistonDocData['size']} */ size;
  /** @type {PistonDocData['flashGap']} */ flashGap;

  /**
   * Create a piston.
   * @param {any} param0 - Raw Firestore document.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  constructor({
    instance, size, flashGap, ...props
  }) {
    const validatedData = pistonSchema.validateSync({
      instance, size, flashGap, ...props,
    });

    super(validatedData);

    this.instance = validatedData.instance;
    this.size = validatedData.size;
    this.flashGap = validatedData.flashGap;
  }

  /**
   * Converts the {@link Piston} instance to Firestore document data.
   * Piston-specific fields live under `metadata`, like tool cavities.
   * @returns {Omit<PistonDocData, 'id'>} {@link PistonDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    const baseDoc = super.toFirestore({ serverTimestamp });
    return {
      ...baseDoc,
      instance: this.instance,
      metadata: {
        ...baseDoc.metadata,
        size: this.size,
        flashGap: this.flashGap,
      },
    };
  }

  /**
   * Creates a {@link Piston} instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns {Piston} A {@link Piston} instance.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  static fromFirestore(docData) {
    const {
      size,
      flashGap,
      linkedParts,
      components,
      ...restMetadata
    } = docData.metadata || {};
    return new Piston({
      ...docData,
      size,
      flashGap,
      linkedParts,
      components,
      metadata: restMetadata || {},
    });
  }
}
